import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiSend } from "react-icons/fi";
import { Card, Button, DataTable, ConfirmDialog } from "../common";
import useAsync from "../../hooks/useAsync";
import ProposalFormModal from "./ProposalFormModal";
import { listProposals, submitProposal, withdrawProposal } from "../../services/kraKpiProposalApi";
import { getErrorMessage } from "../../utils/errors";
import { formatDate } from "../../utils/format";

export default function MyProposalsCard({ recordId, sheetOpen = true, cycleName, dueDefault, onChanged, addSignal = 0 }) {
  const list = useAsync(() => listProposals({ view: "mine" }), []);
  const [form, setForm] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(null);
  // The page-header "Add KRA / KPI" button bumps addSignal; open the same Add form from here.
  useEffect(() => { if (addSignal > 0) setForm({ mode: "create" }); }, [addSignal]);
  const rows = list.data || [];
  const openKraProposals = rows.filter((p) => p.type === "KRA" && ["DRAFT", "PENDING_APPROVAL", "RETURNED", "APPROVED"].includes(p.status) && (!p.recordId || p.recordId === recordId));

  const refresh = () => { list.reload(); onChanged?.(); };

  const send = async (p) => {
    setBusy(p.id);
    try {
      await submitProposal(p.id);
      toast.success("Sent to your manager for approval.");
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async () => {
    setBusy(withdrawing.id);
    try {
      await withdrawProposal(withdrawing.id);
      toast.success("Proposal withdrawn.");
      setWithdrawing(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      title="Add my KRAs & KPIs"
      subtitle="You add your own KRAs and KPIs and send them for approval. Your reporting manager approves, edits, returns or rejects them."
      padded={false}
      actions={<Button size="sm" icon={FiPlus} onClick={() => setForm({ mode: "create" })}>Add KRA / KPI</Button>}
    >
      <DataTable
        dense
        label="My KRA and KPI proposals"
        rows={rows}
        loading={list.loading && !list.data}
        emptyTitle={list.error ? "Proposals not available" : "No proposals yet"}
        emptyText={list.error || "Use Add KRA / KPI to add your first one."}
        columns={[
          {
            key: "title", label: "Proposal",
            render: (p) => (
              <span className="cell-stack">
                <strong>{p.type}: {p.title}</strong>
                <small>
                  {p.type === "KPI" && (p.parentProposalTitle || p.kraName) ? `Under ${p.parentProposalTitle || p.kraName}. ` : ""}
                  {p.recordId ? `${p.weightage || "—"}% weight, target ${p.target || "—"} ${p.unit}${p.dueDate ? `, due ${formatDate(p.dueDate)}` : ""}` : "Library only"}
                </small>
              </span>
            ),
          },
          {
            key: "status", label: "Status",
            render: (p) => (
              <span className="cell-stack">
                <span className={`badge badge-${p.tone}`}><span className="badge-dot" />{p.statusLabel}</span>
                {p.decisionReason && ["RETURNED", "REJECTED", "APPROVED"].includes(p.status) && <small>{p.decidedBy ? `${p.decidedBy}: ` : ""}{p.decisionReason}</small>}
              </span>
            ),
          },
          {
            key: "act", label: "", align: "right",
            render: (p) => (
              <span className="row-actions">
                {["DRAFT", "RETURNED"].includes(p.status) && (
                  <>
                    <Button size="sm" variant="ghost" icon={FiEdit2} aria-label={`Edit ${p.title}`} onClick={() => setForm({ mode: "edit", proposal: p })} />
                    <Button size="sm" variant="secondary" icon={FiSend} loading={busy === p.id} onClick={() => send(p)}>Send</Button>
                  </>
                )}
                {["DRAFT", "PENDING_APPROVAL", "RETURNED"].includes(p.status) && (
                  <button type="button" className="link-btn" onClick={() => setWithdrawing(p)}>Withdraw</button>
                )}
              </span>
            ),
          },
        ]}
      />
      <ProposalFormModal
        open={Boolean(form)}
        mode={form?.mode}
        proposal={form?.proposal}
        recordId={recordId}
        sheetOpen={sheetOpen}
        cycleName={cycleName}
        dueDefault={dueDefault}
        openKraProposals={openKraProposals}
        onClose={() => setForm(null)}
        onSaved={refresh}
      />
      <ConfirmDialog
        open={Boolean(withdrawing)}
        title={`Withdraw "${withdrawing?.title}"?`}
        message="Your manager will no longer see it. You can propose it again later."
        confirmLabel="Withdraw"
        tone="danger"
        loading={Boolean(busy)}
        onConfirm={withdraw}
        onClose={() => setWithdrawing(null)}
      />
    </Card>
  );
}